import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  BenefitCategoryType,
  BenefitItemType,
  BenefitTierType,
  PartnerProgramBenefitsListType,
} from "./Types";

type PartnerProgramBenefitsStateType = {
  categories: PartnerProgramBenefitsListType;
  tierDetails: BenefitTierType[];
  isEditing: boolean;
  draftCategories: PartnerProgramBenefitsListType;
  status: {
    benefitsLoading: boolean;
    benefitsErrorMessage: string;
    saveBenefitsLoading: boolean;
  };
};

const initialState: PartnerProgramBenefitsStateType = {
  categories: [],
  tierDetails: [],
  isEditing: false,
  draftCategories: [],
  status: {
    benefitsLoading: false,
    benefitsErrorMessage: "",
    saveBenefitsLoading: false,
  },
};

const getSortedBenefits = (benefits: BenefitItemType[]) => {
  return [...benefits]
    .sort((a, b) => a.order - b.order)
    .map((item, index) => ({
      ...item,
      order: index + 1,
    }));
};

const reindexBenefitsInCurrentOrder = (benefits: BenefitItemType[]) => {
  return benefits.map((item, index) => ({
    ...item,
    order: index + 1,
  }));
};

const partnerProgramBenefitsSlice = createSlice({
  name: "partner_program_benefits",
  initialState,
  reducers: {
    setPartnerProgramBenefitsLoading: (state, action: PayloadAction<boolean>) => {
      state.status.benefitsLoading = action.payload;
    },
    setPartnerProgramBenefitsErrorMessage: (
      state,
      action: PayloadAction<string>
    ) => {
      state.status.benefitsErrorMessage = action.payload;
    },
    setPartnerProgramBenefitsList: (
      state,
      action: PayloadAction<PartnerProgramBenefitsListType>
    ) => {
      state.categories = action.payload;
    },
    setPartnerProgramBenefitsTierDetails: (
      state,
      action: PayloadAction<BenefitTierType[]>
    ) => {
      state.tierDetails = action.payload;
    },
    setBenefitsSaveLoading: (state, action: PayloadAction<boolean>) => {
      state.status.saveBenefitsLoading = action.payload;
    },
    toggleBenefitsCategoryCollapse: (state, action: PayloadAction<string>) => {
      const toggleCategory = (list: PartnerProgramBenefitsListType) =>
        list.map((category) =>
          category.id === action.payload
            ? { ...category, isCollapsed: !category.isCollapsed }
            : category
        );

      if (state.isEditing) {
        state.draftCategories = toggleCategory(state.draftCategories);
        return;
      }

      state.categories = toggleCategory(state.categories);
    },
    enterBenefitsEditMode: (state) => {
      state.isEditing = true;
      state.draftCategories = state.categories.map((category) => ({
        ...category,
        benefits: category.benefits.map((benefit) => ({
          ...benefit,
          activeTierIds: [...benefit.activeTierIds],
        })),
      }));
    },
    cancelBenefitsEditMode: (state) => {
      state.isEditing = false;
      state.draftCategories = [];
    },
    updateBenefitsDraftTitle: (
      state,
      action: PayloadAction<{ categoryId: string; value: string }>
    ) => {
      if (!state.isEditing) return;
      state.draftCategories = state.draftCategories.map((category) =>
        category.id === action.payload.categoryId
          ? { ...category, title: action.payload.value }
          : category
      );
    },
    updateBenefitsDraftDescription: (
      state,
      action: PayloadAction<{
        categoryId: string;
        benefitId: string;
        description: string;
      }>
    ) => {
      if (!state.isEditing) return;
      state.draftCategories = state.draftCategories.map((category) =>
        category.id === action.payload.categoryId
          ? {
              ...category,
              benefits: category.benefits.map((benefit) =>
                benefit.id === action.payload.benefitId
                  ? { ...benefit, description: action.payload.description }
                  : benefit
              ),
            }
          : category
      );
    },
    toggleBenefitsDraftTierFlag: (
      state,
      action: PayloadAction<{
        categoryId: string;
        benefitId: string;
        tierId: number;
      }>
    ) => {
      if (!state.isEditing) return;
      state.draftCategories = state.draftCategories.map((category) =>
        category.id === action.payload.categoryId
          ? {
              ...category,
              benefits: category.benefits.map((benefit) =>
                benefit.id === action.payload.benefitId
                  ? {
                      ...benefit,
                      activeTierIds: benefit.activeTierIds.includes(
                        action.payload.tierId
                      )
                        ? benefit.activeTierIds.filter(
                            (tierId) => tierId !== action.payload.tierId
                          )
                        : [...benefit.activeTierIds, action.payload.tierId],
                    }
                  : benefit
              ),
            }
          : category
      );
    },
    reorderBenefitsDraftRows: (
      state,
      action: PayloadAction<{ categoryId: string; fromIndex: number; toIndex: number }>
    ) => {
      if (!state.isEditing) return;
      state.draftCategories = state.draftCategories.map((category) => {
        if (category.id !== action.payload.categoryId) return category;
        const nextRows = [...category.benefits];
        const { fromIndex, toIndex } = action.payload;
        if (
          fromIndex < 0 ||
          toIndex < 0 ||
          fromIndex >= nextRows.length ||
          toIndex >= nextRows.length
        ) {
          return category;
        }
        const [movedItem] = nextRows.splice(fromIndex, 1);
        nextRows.splice(toIndex, 0, movedItem);
        return {
          ...category,
          benefits: reindexBenefitsInCurrentOrder(nextRows),
        };
      });
    },
    deleteBenefitsDraftRow: (
      state,
      action: PayloadAction<{ categoryId: string; benefitId: string }>
    ) => {
      if (!state.isEditing) return;
      state.draftCategories = state.draftCategories.map((category) =>
        category.id === action.payload.categoryId
          ? {
              ...category,
              benefits: getSortedBenefits(
                category.benefits.filter(
                  (benefit) => benefit.id !== action.payload.benefitId
                )
              ),
            }
          : category
      );
    },
    addBenefitsCategory: (
      state,
      action: PayloadAction<{ title: string; categoryId?: string }>
    ) => {
      const categoryId = action.payload.categoryId || `category-${Date.now()}`;
      const nextCategory: BenefitCategoryType = {
        id: categoryId,
        title: action.payload.title,
        order: 0,
        isCollapsed: false,
        benefits: [],
      };
      if (state.isEditing) {
        state.draftCategories = [
          ...state.draftCategories,
          { ...nextCategory, order: state.draftCategories.length + 1 },
        ];
        return;
      }
      state.categories = [
        ...state.categories,
        { ...nextCategory, order: state.categories.length + 1 },
      ];
    },
    deleteBenefitsCategory: (state, action: PayloadAction<string>) => {
      const updateCategoryOrder = (list: PartnerProgramBenefitsListType) =>
        list
        .filter((category) => category.id !== action.payload)
        .map((category, index) => ({
          ...category,
          order: index + 1,
        }));
      if (state.isEditing) {
        state.draftCategories = updateCategoryOrder(state.draftCategories);
        return;
      }
      state.categories = updateCategoryOrder(state.categories);
    },
    removeBenefitsCategoriesByIds: (state, action: PayloadAction<string[]>) => {
      const ids = new Set(action.payload);
      const updateCategoryOrder = (list: PartnerProgramBenefitsListType) =>
        list
          .filter((category) => !ids.has(category.id))
          .map((category, index) => ({
            ...category,
            order: index + 1,
          }));
      if (state.isEditing) {
        state.draftCategories = updateCategoryOrder(state.draftCategories);
        return;
      }
      state.categories = updateCategoryOrder(state.categories);
    },
    addBenefitToCategory: (
      state,
      action: PayloadAction<{
        categoryId: string;
        name: string;
        description: string;
        benefitsType: string;
        benefitsStatus: number;
        deliveryType: string;
        weightage: number | null;
        frequency: string;
      }>
    ) => {
      const newBenefit: BenefitItemType = {
        id: `benefit-${Date.now()}`,
        apiId: 0,
        name: action.payload.name,
        description: action.payload.description,
        order: 0,
        activeTierIds: [],
        benefitsType: action.payload.benefitsType,
        benefitsStatus: action.payload.benefitsStatus,
        deliveryType: action.payload.deliveryType,
        weightage: action.payload.weightage,
        frequency: action.payload.frequency,
      };

      if (state.isEditing) {
        state.draftCategories = state.draftCategories.map((category) => {
          if (category.id !== action.payload.categoryId) return category;
          return {
            ...category,
            benefits: getSortedBenefits([
              ...category.benefits,
              { ...newBenefit, order: category.benefits.length + 1 },
            ]),
          };
        });
        return;
      }

      state.categories = state.categories.map((category) => {
        if (category.id !== action.payload.categoryId) return category;
        return {
          ...category,
          benefits: getSortedBenefits([
            ...category.benefits,
            { ...newBenefit, order: category.benefits.length + 1 },
          ]),
        };
      });
    },
    updateBenefitInCategory: (
      state,
      action: PayloadAction<{
        categoryId: string;
        benefitId: string;
        name: string;
        description: string;
        benefitsType: string;
        benefitsStatus: number;
        deliveryType: string;
        weightage: number | null;
        frequency: string;
      }>
    ) => {
      const updateBenefits = (benefits: BenefitItemType[]) =>
        benefits.map((benefit) =>
          benefit.id === action.payload.benefitId
            ? {
                ...benefit,
                name: action.payload.name,
                description: action.payload.description,
                benefitsType: action.payload.benefitsType,
                benefitsStatus: action.payload.benefitsStatus,
                deliveryType: action.payload.deliveryType,
                weightage: action.payload.weightage,
                frequency: action.payload.frequency,
              }
            : benefit
        );

      if (state.isEditing) {
        state.draftCategories = state.draftCategories.map((category) =>
          category.id === action.payload.categoryId
            ? { ...category, benefits: updateBenefits(category.benefits) }
            : category
        );
        return;
      }

      state.categories = state.categories.map((category) =>
        category.id === action.payload.categoryId
          ? { ...category, benefits: updateBenefits(category.benefits) }
          : category
      );
    },
    saveBenefitsCategoryChanges: (state) => {
      if (!state.isEditing) return;
      state.categories = state.draftCategories.map((category) => ({
        ...category,
        benefits: getSortedBenefits(category.benefits),
      }));
      state.isEditing = false;
      state.draftCategories = [];
    },
  },
});

export const partnerProgramBenefitsSliceReducer = partnerProgramBenefitsSlice.reducer;
export const partnerProgramBenefitsSliceAction = partnerProgramBenefitsSlice.actions;
